import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Actions from '../Actions';
import Button from '../Button';
import Empty from '../Empty';
import FontAwesomeIcon from '../Icon';
import PropertyForm from '../PropertyForm';
import Table from '../Table';
import {
  setPropertyFormData,
  clearPropertyFormData,
} from 'src/store/actions/propertyForm';
import { selectSearch } from 'src/store/selectors/search';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { exitEasyWalkThroughForm } from 'src/store/actions/easyWalkThroughForm';
import { exitPropertyWalkThroughForm } from 'src/store/actions/propertyWalkThroughForm';
import LoadState from 'src/components/LoadState/LoadState';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { setSelectedModulePermission } from 'src/store/actions/tabSelections';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import SparkSyncTriggerButton from 'src/components/SparkSyncTriggerButton/SparkSyncTriggerButton';
import { createPropertiesTableColumns, TableColumn } from './helpers';
import Search from 'src/components/Search/Search';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';

const SKELETON_ROW_COUNT: number = 12;

const PropertiesTable = ({
  properties,
  isLoadingProperties = false,
  onSaveProperty,
  onSearch,
  onDeleteProperty,
  tabList,
  clearSelectedModulePermission,
  onWalkthroughSyncToggle
}: any) => {
  const dispatch = useDispatch();

  const [showOverlay, setShowOverlay] = useState(false);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    property: null as any,
  });
  const [formInitialData, setFormInitialData] = useState<any>(undefined);
  const [formInitialModulePermissions, setFormInitialModulePermissions] =
    useState<any[]>([]);

  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const search = useSelector(selectSearch);
  const user = useSelector(selectAuthenticatedUser);
  const loader = useSelector(selectFormLoader);

  const userRoleName: any = user.roles[0].role.name;
  const hasSparkProperties: any = properties.some((p: any) => p.isSpark);
  const isAdminOrSuperAdmin: boolean =
    userRoleName === 'Admin' || userRoleName === 'Super Admin';

  const onSetPropertyFormData = (data: any) => {
    dispatch(setPropertyFormData(data));

    let modulePermissions = [];

    if (data.modulePermissions !== null) {
      modulePermissions = data.modulePermissions.map((item: any) => ({
        Id: item.id,
      }));
    }

    dispatch(setSelectedModulePermission(modulePermissions));
    setFormInitialData(data);
    setFormInitialModulePermissions(modulePermissions);
  };

  const handleNavigateToProperty = () => {
    dispatch(exitPropertyWalkThroughForm());
    dispatch(exitEasyWalkThroughForm());
  };

  const handleEditProperty = (data: any) => {
    onSetPropertyFormData(data);
    setShowOverlay(true);
  };

  const handleOpenDeleteModal = (data: any) => {
    setDeleteModalState({ isOpen: true, property: data });
  };

  const propertiesTableColumns: TableColumn[] = useMemo(() => {
    return createPropertiesTableColumns({
      userRoleName,
      onEditProperty: handleEditProperty,
      onDeleteProperty: handleOpenDeleteModal,
      onNavigateToProperty: handleNavigateToProperty,
      onWalkthroughSyncToggle
    });
  }, [userRoleName, properties]);

  const skeletonColumns: TableColumn[] = useMemo(
    () =>
      propertiesTableColumns.map((c: TableColumn) => ({
        ...c,
        cell: () => <Skeleton variant="text" width="70%" />,
      })),
    [propertiesTableColumns]
  );

  const skeletonData: any[] = useMemo(
    () =>
      Array.from({ length: SKELETON_ROW_COUNT }, (_: unknown, i: number) => ({
        id: `properties-skeleton-${i}`,
      })),
    []
  );

  const isLoading: boolean = isLoadingProperties || loader;
  const isEmpty: boolean = !isLoading && properties.length === 0;

  const emptyNode: React.ReactNode = (
    <Empty
      direction="ttb"
      headline="No properties yet"
      strapline="All properties you add will be listed here so it's easier for you to manage."
      searchIsActive={search && search.properties ? search.properties : ''}
      cta={
        userRoleName === 'Admin' && (
          <Button
            color="teal"
            label="Add Property"
            onClick={() => {
              dispatch(
                setPropertyFormData({
                  name: '',
                  addressLine1: '',
                  city: '',
                  province: '',
                  postalCode: '',
                })
              );
              setFormInitialData(undefined);
              setFormInitialModulePermissions([]);
              setShowOverlay(true);
            }}
            icon={
              <FontAwesomeIcon glyph="building" weight="light" size="small" />
            }
            size="small"
          />
        )
      }
      image={
        <img alt="No properties yet" src="/assets/empty/properties.svg" />
      }
    />
  );

  return (
    <>
      <Actions compact>
        <Search
          size="small"
          placeholder="Search Properties..."
          onChange={onSearch}
          theme={undefined}
        />
        <div>
          {hasSparkProperties && <SparkSyncTriggerButton />}
          {isAdminOrSuperAdmin && (
            <Button
              color="teal"
              label="Add Property"
              onClick={() => {
                setFormInitialData(undefined);
                setFormInitialModulePermissions([]);
                setShowOverlay(true);
              }}
              icon={
                <FontAwesomeIcon
                  glyph="building"
                  weight="light"
                  size="small"
                />
              }
              size="small"
            />
          )}
        </div>
      </Actions>
      <LoadState
        isLoading={false}
        isEmpty={isEmpty}
        emptyNode={emptyNode}
      >
        <Table
          columns={isLoading ? skeletonColumns : propertiesTableColumns}
          data={isLoading ? skeletonData : properties}
          paginate={{ itemsPerPage, position: 'both' }}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      </LoadState>
      {deleteModalState.isOpen && (
        <ConfirmationModal
          variant="danger"
          title={`Delete Property ${deleteModalState.property?.name}?`}
          confirmLabel="Yes, Delete Everything"
          cancelLabel="Go Back"
          requireTypeToConfirm={true}
          confirmationWord="DELETE"
          message={
            <div>
              <p>
                You are about to <strong>permanently delete</strong> this entire
                property.
              </p>
              <p style={{ marginTop: '8px' }}>
                Doing so will immediately destroy all data associated with{' '}
                <strong>{deleteModalState.property?.name}</strong>, including:
              </p>
              <ul
                style={{
                  textAlign: 'left',
                  display: 'inline-block',
                  marginTop: '8px',
                }}
              >
                <li>All Buildings and Suites</li>
                <li>All Associated Homeowner and Occupant records</li>
                <li>
                  Associated Walkthrough history, Signatures, and Documents
                </li>
                <li>All Associated Service Requests and Notes</li>
              </ul>
            </div>
          }
          warningMessage="This action is irreversible. All data will be lost forever."
          onCancel={() =>
            setDeleteModalState({ isOpen: false, property: null })
          }
          onConfirm={() => {
            onDeleteProperty(deleteModalState.property);
            setDeleteModalState({ isOpen: false, property: null });
          }}
        />
      )}
      {showOverlay && (
        <PropertyForm
          tabList={tabList}
          onClose={() => {
            setShowOverlay(false);
            dispatch(clearPropertyFormData());
            clearSelectedModulePermission();
            setFormInitialData(undefined);
            setFormInitialModulePermissions([]);
          }}
          onSaveProperty={onSaveProperty}
          initialData={formInitialData}
          initialModulePermissions={formInitialModulePermissions}
          onModulePermissionsChange={(perms: any[]) =>
            dispatch(setSelectedModulePermission(perms))
          }
        />
      )}
    </>
  );
};

export default PropertiesTable;
